import Image from "next/image";
import { IconPlus } from "@tabler/icons-react";
import clsx from "clsx";

import { ButtonCircle, Card } from "@chef/components";
import { useTrack } from "@chef/state-management/hooks";
import { useFlag } from "@chef/feature-flags";
import { FEATURE_FLAGS } from "@chef/constants";
import { useMediaQuery } from "@chef/hooks";
import { isEqualStrings, isNotEqualStrings } from "@chef/utils/equal";

import { intl } from "./UpsaleCard.Intl";

interface UpsaleCardProps {
  href?: string;
  scrollToId?: string;
  imgUrl: string;
  deliveryStatus?: "active" | "paused" | "freezed";
}

const UpsaleCard = ({
  href,
  imgUrl,
  scrollToId,
  deliveryStatus,
}: UpsaleCardProps) => {
  const track = useTrack();
  const { enabled: isWeekEditorRedesignEnabled } = useFlag(
    FEATURE_FLAGS.WEEK_EDITOR_REDESIGN,
  );

  const [isMobile] = useMediaQuery(`(max-width: 768px )`);

  let text = `${intl.ADD} ${intl.DISH}`;
  if (deliveryStatus) {
    if (isEqualStrings(deliveryStatus, "paused")) {
      text = intl.ORDER_DELIVERY_TO_CHOOSE_DISHES;
    }

    if (isEqualStrings(deliveryStatus, "freezed")) {
      text = intl.REACTIVATE_YOUR_ACCOUNT_TO_CHOOSE_DISHES;
    }
  }
  if (isWeekEditorRedesignEnabled && isMobile) {
    text = intl.ADD_UP_TO_N_DISHES;
  }

  const handleButtonClicked = () => {
    if (!href && scrollToId) {
      const el = document.getElementById(scrollToId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
    track("buttonClicked", {
      affiliation: "Frontend process",
      button_colour: "outlined",
      button_placement: "upsale-card",
      button_source: "code",
      button_text: text,
    });
  };

  return (
    <Card
      className={clsx(
        "relative flex items-center justify-center flex-grow w-full h-full px-2 text-center aspect-1/2 md:px-12 lg:px-6",
        isNotEqualStrings(deliveryStatus, "active") &&
          !isWeekEditorRedesignEnabled &&
          "opacity-50",
      )}
      noPadding
      transparent
    >
      <Image
        src={imgUrl}
        fill
        className="object-cover rounded"
        sizes="5vw"
        alt=""
        priority={false}
      />
      <div className="absolute m-px rounded -inset-px bg-black/40 backdrop-blur-sm" />

      <div className="flex flex-col items-center gap-3 text-white z-5">
        {((deliveryStatus && isEqualStrings(deliveryStatus, "active")) ||
          isWeekEditorRedesignEnabled) && (
          <ButtonCircle
            className="!w-20 !h-20 border border-white !bg-transparent"
            Icon={() => (
              <IconPlus
                stroke={1}
                width={44}
                height={44}
                className="text-white"
              />
            )}
            href={href}
            onClick={handleButtonClicked}
          />
        )}
        <strong className="mt-2 text-sm">{text}</strong>
      </div>
    </Card>
  );
};

export default UpsaleCard;
